import React, { useEffect, useState } from 'react';
import BackButton from '../components/BackButton';

const BG = '#FCF4E4';
const INK = '#111010';
const FONT_DISPLAY = "'Archivo', sans-serif";
const FONT_BODY = "'Outfit', system-ui, sans-serif";
const FONT_MONO = "'JetBrains Mono', monospace";
const FLIP_MS = 620;

// Every race I've finished, newest first.
const RACES = [
  {
    year: '2026',
    name: "Nagoya Women's Marathon",
    place: 'Nagoya, Japan',
    dist: '42.195K',
    time: '3:54:07',
    paceLabel: '',
    symbol: '#m-nagoya',
    grad: 'linear-gradient(140deg, #FFCE86 0%, #D9A05B 100%)',
    blurb: "My first full road marathon. I signed up thinking Tiffany & Co. is still the race sponsor. Turned out they dropped as a sponsor the year before but thank god I didn't realize till after paying the registration fee. The race was super well organized, so many people cheering on the streets, and running past Nagoya castle felt amazing. It motivated me to push myself even further and I signed up for an ultra.",
  },
  {
    year: '2024',
    name: 'Run2MTL',
    place: 'Québec, Canada',
    dist: '650K',
    time: '54:37:00',
    paceLabel: '11 person relay',
    symbol: '#m-mtl',
    grad: 'linear-gradient(140deg, #7FB8EA 0%, #5C7FE0 100%)',
    blurb: 'This was a 650K relay race starting at CN tower in Toronto and ending at the top of Mount Royal in Montreal. We ran as a team of 11 over the course of two days. The running wasn’t as challenging as figuring out the logistics and bearing though being in a stinky camper van. The take away from this race was new friendships and we still refer to ourselves as bagel runners and organize a relay for ourselves every year since.',
  },
  {
    year: '2021',
    name: 'Ultra-Trail Harricana 65K',
    place: 'Charlevoix, Canada',
    dist: '65K',
    time: '11:16:12',
    paceLabel: '10:24 / km',
    symbol: '#m-ultra',
    grad: 'linear-gradient(140deg, #C98BF2 0%, #E85FD0 100%)',
    blurb:
      "My first ever ultra. COVID lockdowns meant a lot of time on my hands and being back with my family in rural Japan meant easy access to mountains for some vertical training. I read about the Sahara marathon and I was motivated to chase the ultimate runners high. I’ve only ever ran half marathon races till then so this was a big jump. I finished it with the last 10k feeling like years. Having ran an ultra gave me a lot of confidence for years when it came to endurance and what I’m capable of.",
  },
  {
    year: '2020',
    name: 'Sky Half Sugadaira',
    place: 'Nagano, Japan',
    dist: '+1190 m',
    time: '3:29:06',
    paceLabel: '1190 m vert',
    symbol: '#m-sky',
    grad: 'linear-gradient(140deg, #FFCE86 0%, #FF9C7C 100%)',
    blurb:
      'Same distance as road half, twice the time. My first vertical race. I ran up the mountain in Nagano. Some very rocky parts where I had to use hands. The trail love that started with my high school cross country finally had somewhere real to go.',
  },
  {
    year: '2019',
    name: 'Toronto Waterfront 10K',
    place: 'Toronto, Canada',
    dist: '10K',
    time: '49:46',
    paceLabel: '4:59 / km',
    symbol: '#m-10k',
    grad: 'linear-gradient(140deg, #8CEBD8 0%, #7FB8EA 100%)',
    blurb:
      'Running clicked in middle school after I randomly entered a 5K cross country meet and won it. This is another random 10k I decided to enter one year. Ego mostly kept me going and got a pretty good time thanks to it.',
  },
  {
    year: '2019',
    name: 'Scotiabank Toronto Waterfront Half',
    place: 'Toronto, Canada',
    dist: '21.1K',
    time: '1:51:22',
    paceLabel: '5:17 / km',
    symbol: '#m-half',
    grad: 'linear-gradient(140deg, #FF8FB4 0%, #EE3F9E 100%)',
    blurb:
      'I managed to convince one of my university friend to run it with me. We both barely trained but having an accountability partner made sure I showed up and ran the full thing.',
  },
];

// Medal artwork, defined once and referenced by id via <use>.
const MedalDefs = () => (
  <svg width="0" height="0" aria-hidden="true" style={{ position: 'absolute', pointerEvents: 'none' }}>
    <defs>
      <linearGradient id="silver" x1="0" y1="0" x2="0.7" y2="1">
        <stop offset="0" stopColor="#F2F0EB" />
        <stop offset="0.45" stopColor="#D6D3CB" />
        <stop offset="0.72" stopColor="#EDEBE5" />
        <stop offset="1" stopColor="#B7B3AA" />
      </linearGradient>
      <linearGradient id="silverIn" x1="0" y1="0" x2="0.6" y2="1">
        <stop offset="0" stopColor="#E4E1DA" />
        <stop offset="1" stopColor="#CBC8C0" />
      </linearGradient>
      <linearGradient id="wood" x1="0" y1="0" x2="0.6" y2="1">
        <stop offset="0" stopColor="#E7C9F6" />
        <stop offset="1" stopColor="#E187D2" />
      </linearGradient>
      <linearGradient id="ribTeal" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#8CEBD8" />
        <stop offset="1" stopColor="#7FB8EA" />
      </linearGradient>
      <linearGradient id="ribPink" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#FF8FB4" />
        <stop offset="1" stopColor="#EE3F9E" />
      </linearGradient>
      <linearGradient id="ribOrange" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#FFCE86" />
        <stop offset="1" stopColor="#FF9C7C" />
      </linearGradient>
      <linearGradient id="ribBlue" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#7FB8EA" />
        <stop offset="1" stopColor="#5C7FE0" />
      </linearGradient>
      <linearGradient id="ribGold" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#FFCE86" />
        <stop offset="1" stopColor="#D9A05B" />
      </linearGradient>
      <linearGradient id="ribPurple" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#C98BF2" />
        <stop offset="1" stopColor="#E85FD0" />
      </linearGradient>

      <symbol id="m-10k" viewBox="0 0 200 252">
        <rect x="70" y="0" width="60" height="58" rx="5" fill="url(#ribTeal)" />
        <rect x="70" y="42" width="60" height="9" fill="#111010" opacity="0.14" />
        <circle cx="100" cy="152" r="94" fill="url(#silver)" stroke="#A5A29A" strokeWidth="2" />
        <circle cx="100" cy="152" r="79" fill="none" stroke="#B5B2AA" strokeWidth="1.5" />
        <circle cx="100" cy="152" r="60" fill="url(#silverIn)" stroke="url(#ribTeal)" strokeWidth="4" />
        <path id="arc10k" d="M 100 152 m -70 0 a 70 70 0 1 1 140 0" fill="none" />
        <text fontFamily="Archivo, sans-serif" fontWeight="700" fontSize="14" letterSpacing="2.6" fill="#7C7870">
          <textPath href="#arc10k" startOffset="50%" textAnchor="middle">TORONTO WATERFRONT</textPath>
        </text>
        <text x="100" y="152" textAnchor="middle" fontFamily="Archivo, sans-serif" fontWeight="900" fontSize="46" letterSpacing="-1" fill="#3B3934">10K</text>
        <text x="100" y="180" textAnchor="middle" fontFamily="Archivo, sans-serif" fontWeight="700" fontSize="13" letterSpacing="4" fill="#8B877E">2019</text>
        <circle cx="100" cy="204" r="5" fill="url(#ribTeal)" />
      </symbol>

      <symbol id="m-half" viewBox="0 0 200 252">
        <circle cx="100" cy="26" r="14" fill="none" stroke="#B5B2AA" strokeWidth="7" />
        <path d="M76 40 L124 40 L116 82 L84 82 Z" fill="url(#ribPink)" />
        <path d="M76 40 L124 40 L122 50 L78 50 Z" fill="#FFFFFF" opacity="0.35" />
        <circle cx="100" cy="158" r="90" fill="url(#silver)" stroke="#A5A29A" strokeWidth="2" />
        <circle cx="100" cy="158" r="90" fill="none" stroke="url(#ribPink)" strokeWidth="5" />
        <circle cx="100" cy="158" r="46" fill="#FCF4E4" stroke="#B5B2AA" strokeWidth="2" />
        <circle cx="100" cy="158" r="60" fill="none" stroke="#C4C1B9" strokeWidth="1.5" strokeDasharray="3 7" />
        <path id="archalf" d="M 100 158 m -72 0 a 72 72 0 1 1 144 0" fill="none" />
        <text fontFamily="Archivo, sans-serif" fontWeight="700" fontSize="12.5" letterSpacing="1.8" fill="#7C7870">
          <textPath href="#archalf" startOffset="50%" textAnchor="middle">SCOTIABANK HALF</textPath>
        </text>
        <text x="100" y="156" textAnchor="middle" fontFamily="Archivo, sans-serif" fontWeight="900" fontSize="30" letterSpacing="-1" fill="#3B3934">21.1</text>
        <text x="100" y="176" textAnchor="middle" fontFamily="Archivo, sans-serif" fontWeight="700" fontSize="11" letterSpacing="3" fill="#C0397F">HALF</text>
        <text x="100" y="228" textAnchor="middle" fontFamily="Archivo, sans-serif" fontWeight="700" fontSize="12" letterSpacing="3.5" fill="#8B877E">2019</text>
      </symbol>

      <symbol id="m-sky" viewBox="0 0 200 252">
        <rect x="86" y="8" width="28" height="26" rx="4" fill="url(#ribOrange)" />
        <g fill="url(#silver)" stroke="#A5A29A" strokeWidth="1.5">
          <circle cx="100" cy="60" r="13" />
          <circle cx="139" cy="70" r="13" />
          <circle cx="168" cy="99" r="13" />
          <circle cx="178" cy="138" r="13" />
          <circle cx="168" cy="177" r="13" />
          <circle cx="139" cy="206" r="13" />
          <circle cx="100" cy="216" r="13" />
          <circle cx="61" cy="206" r="13" />
          <circle cx="32" cy="177" r="13" />
          <circle cx="22" cy="138" r="13" />
          <circle cx="32" cy="99" r="13" />
          <circle cx="61" cy="70" r="13" />
        </g>
        <circle cx="100" cy="138" r="82" fill="url(#silver)" stroke="#A5A29A" strokeWidth="2" />
        <circle cx="100" cy="138" r="68" fill="url(#silverIn)" stroke="url(#ribOrange)" strokeWidth="3" />
        <path d="M56 162 L86 116 L104 144 L120 122 L146 162 Z" fill="#B9B5AC" />
        <path d="M86 116 L98 134 L74 134 Z" fill="#F0EEE8" />
        <path id="arcsky" d="M 100 138 m -56 0 a 56 56 0 1 1 112 0" fill="none" />
        <text fontFamily="Archivo, sans-serif" fontWeight="700" fontSize="12.5" letterSpacing="2.2" fill="#7C7870">
          <textPath href="#arcsky" startOffset="50%" textAnchor="middle">SUGADAIRA SKY HALF</textPath>
        </text>
        <text x="100" y="188" textAnchor="middle" fontFamily="Archivo, sans-serif" fontWeight="900" fontSize="20" fill="#3B3934">+1190M</text>
        <text x="100" y="205" textAnchor="middle" fontFamily="Archivo, sans-serif" fontWeight="700" fontSize="11" letterSpacing="3.5" fill="#C8804E">2020</text>
      </symbol>

      <symbol id="m-mtl" viewBox="0 0 200 252">
        <rect x="74" y="0" width="52" height="46" rx="5" fill="url(#ribBlue)" />
        <rect x="74" y="34" width="52" height="8" fill="#111010" opacity="0.14" />
        <path d="M100 42 L172 74 L172 142 C172 192 138 216 100 232 C62 216 28 192 28 142 L28 74 Z" fill="url(#silver)" stroke="#A5A29A" strokeWidth="2" />
        <path d="M100 58 L157 83 L157 141 C157 181 130 201 100 215 C70 201 43 181 43 141 L43 83 Z" fill="none" stroke="url(#ribBlue)" strokeWidth="4" />
        <text x="100" y="112" textAnchor="middle" fontFamily="Archivo, sans-serif" fontWeight="700" fontSize="12" letterSpacing="3.4" fill="#7C7870">RUN 2 MTL</text>
        <text x="100" y="156" textAnchor="middle" fontFamily="Archivo, sans-serif" fontWeight="900" fontSize="42" letterSpacing="-1.5" fill="#3B3934">650K</text>
        <text x="100" y="182" textAnchor="middle" fontFamily="Archivo, sans-serif" fontWeight="700" fontSize="11" letterSpacing="3.4" fill="#4E77C6">2024</text>
      </symbol>

      <symbol id="m-nagoya" viewBox="0 0 200 252">
        <circle cx="100" cy="24" r="12" fill="none" stroke="#B5B2AA" strokeWidth="6" />
        <rect x="38" y="68" width="124" height="124" rx="24" transform="rotate(45 100 130)" fill="url(#silver)" stroke="#A5A29A" strokeWidth="2" />
        <rect x="52" y="82" width="96" height="96" rx="18" transform="rotate(45 100 130)" fill="none" stroke="#C4C1B9" strokeWidth="1.5" />
        <circle cx="100" cy="130" r="50" fill="url(#silverIn)" stroke="url(#ribGold)" strokeWidth="3" />
        <text x="100" y="116" textAnchor="middle" fontFamily="Archivo, sans-serif" fontWeight="700" fontSize="11" letterSpacing="2.6" fill="#7C7870">NAGOYA</text>
        <text x="100" y="146" textAnchor="middle" fontFamily="Archivo, sans-serif" fontWeight="900" fontSize="25" letterSpacing="-1" fill="#3B3934">42.195</text>
        <text x="100" y="165" textAnchor="middle" fontFamily="Archivo, sans-serif" fontWeight="700" fontSize="10.5" letterSpacing="3" fill="#C8804E">2026</text>
      </symbol>

      <symbol id="m-ultra" viewBox="0 0 200 252">
        <path d="M100 26 L184 76 L184 176 L100 226 L16 176 L16 76 Z" fill="url(#wood)" stroke="#B060A8" strokeWidth="2" strokeLinejoin="round" />
        <path d="M100 44 L169 85 L169 167 L100 208 L31 167 L31 85 Z" fill="none" stroke="#FCF4E4" strokeWidth="2" opacity="0.75" />
        <circle cx="100" cy="60" r="10" fill="#FCF4E4" stroke="#B060A8" strokeWidth="2" />
        <path d="M52 150 L78 108 L96 136 L114 104 L148 150 Z" fill="#FCF4E4" opacity="0.55" />
        <text x="100" y="132" textAnchor="middle" fontFamily="Archivo, sans-serif" fontWeight="900" fontSize="42" letterSpacing="-1.5" fill="#5C1F53">65K</text>
        <text x="100" y="152" textAnchor="middle" fontFamily="Archivo, sans-serif" fontWeight="700" fontSize="11" letterSpacing="3.4" fill="#7A2A6E">HARRICANA</text>
        <text x="100" y="172" textAnchor="middle" fontFamily="Archivo, sans-serif" fontWeight="700" fontSize="10" letterSpacing="2" fill="#7A2A6E" opacity="0.8">ULTRA-TRAIL · 2021</text>
      </symbol>
    </defs>
  </svg>
);

const RunPage = () => {
  const [selectedIndex, setSelectedIndex] = useState(null);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setSelectedIndex(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const selected = selectedIndex == null ? null : RACES[selectedIndex];

  return (
    <div
      style={{
        minHeight: '100vh',
        background: BG,
        color: INK,
        fontFamily: FONT_BODY,
        padding: '40px 48px 72px',
        boxSizing: 'border-box',
      }}
    >
      <style>{`
        @keyframes runOverlayIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes runFlipIn { from { transform: scale(.38) rotateY(0deg) } to { transform: scale(1) rotateY(180deg) } }
        .run-medal-btn { transition: transform .22s cubic-bezier(.2,.7,.2,1); }
        .run-medal-btn:hover { transform: translateY(-10px) scale(1.03); }
        .run-close-btn:hover { background: ${INK}; color: #FFFDF7; }
      `}</style>

      <MedalDefs />

      {/* Top bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 32,
          maxWidth: 1240,
          margin: '0 auto',
        }}
      >
        <BackButton color="rgba(17,16,16,.55)" hoverColor={INK} fontFamily={FONT_BODY} />
        <div
          style={{
            fontFamily: FONT_MONO,
            fontSize: 12.5,
            letterSpacing: '.14em',
            textTransform: 'uppercase',
            color: 'rgba(17,16,16,.45)',
            textAlign: 'right',
            paddingTop: 10,
          }}
        >
          For fun / Run · {RACES.length} medals
        </div>
      </div>

      {/* Hero */}
      <div
        style={{
          maxWidth: 1240,
          margin: '44px auto 0',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 48,
          flexWrap: 'wrap',
        }}
      >
        <h1
          style={{
            margin: 0,
            fontFamily: FONT_DISPLAY,
            fontWeight: 900,
            fontSize: 'clamp(52px, 7vw, 104px)',
            lineHeight: 0.86,
            letterSpacing: '-.03em',
            textTransform: 'uppercase',
          }}
        >
          Running
        </h1>
        <p
          style={{
            margin: '0 0 8px',
            maxWidth: 340,
            fontSize: 16.5,
            lineHeight: 1.5,
            color: 'rgba(17,16,16,.62)',
          }}
        >
          Running has been a hobby of mine ever since I joined my high school XC team. Here are some memorable races I've finished. Tap a medal to flip it over.
        </p>
      </div>

      {/* Medal grid */}
      <div
        style={{
          maxWidth: 1240,
          margin: '52px auto 0',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
          gap: '40px 30px',
          alignItems: 'start',
        }}
      >
        {RACES.map((race, idx) => (
          <button
            key={race.name}
            type="button"
            onClick={() => setSelectedIndex(idx)}
            aria-label={race.name}
            className="run-medal-btn"
            style={{
              all: 'unset',
              boxSizing: 'border-box',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 8,
            }}
          >
            <svg
              viewBox="0 0 200 252"
              style={{ width: '100%', maxWidth: 220, height: 'auto', filter: 'drop-shadow(0 14px 18px rgba(17,16,16,.24))' }}
            >
              <use href={race.symbol} />
            </svg>
          </button>
        ))}
      </div>

      {/* Flip-card modal */}
      {selected && (
        <div
          onClick={() => setSelectedIndex(null)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 32,
            background: 'rgba(26,18,6,.6)',
            backdropFilter: 'blur(3px)',
            animation: 'runOverlayIn .22s ease both',
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ perspective: 1800, width: 'min(660px, 100%)' }}>
            <div
              style={{
                position: 'relative',
                width: '100%',
                aspectRatio: '1 / .74',
                transformStyle: 'preserve-3d',
                animation: `runFlipIn ${FLIP_MS}ms cubic-bezier(.2,.75,.2,1) both`,
              }}
            >
              {/* Front: medal */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg viewBox="0 0 200 252" style={{ height: '96%', width: 'auto', filter: 'drop-shadow(0 18px 24px rgba(17,16,16,.4))' }}>
                  <use href={selected.symbol} />
                </svg>
              </div>

              {/* Back: details */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                  transform: 'rotateY(180deg)',
                  borderRadius: 26,
                  overflow: 'hidden',
                  background: '#FFFDF7',
                  boxShadow: '0 40px 90px -30px rgba(17,16,16,.6)',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div style={{ height: 9, flex: 'none', background: selected.grad }} />
                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    padding: '28px 34px 26px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 15,
                    overflow: 'auto',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 16,
                      fontFamily: FONT_MONO,
                      fontSize: 12,
                      letterSpacing: '.14em',
                      textTransform: 'uppercase',
                      color: 'rgba(17,16,16,.5)',
                    }}
                  >
                    <span>{selected.year} · {selected.place} · {selected.dist}</span>
                    <button
                      type="button"
                      onClick={() => setSelectedIndex(null)}
                      className="run-close-btn"
                      style={{
                        all: 'unset',
                        cursor: 'pointer',
                        border: '1px solid rgba(17,16,16,.28)',
                        borderRadius: 6,
                        padding: '4px 9px',
                        fontFamily: FONT_MONO,
                        fontSize: 11,
                        letterSpacing: '.12em',
                      }}
                    >
                      Close
                    </button>
                  </div>
                  <h2
                    style={{
                      margin: 0,
                      fontFamily: FONT_DISPLAY,
                      fontWeight: 900,
                      fontSize: 'clamp(26px, 3.2vw, 38px)',
                      lineHeight: 0.98,
                      letterSpacing: '-.025em',
                      textTransform: 'uppercase',
                      color: INK,
                    }}
                  >
                    {selected.name}
                  </h2>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 14,
                      borderTop: '1px solid rgba(17,16,16,.12)',
                      borderBottom: '1px solid rgba(17,16,16,.12)',
                      padding: '14px 0',
                    }}
                  >
                    <div
                      style={{
                        fontFamily: FONT_DISPLAY,
                        fontWeight: 900,
                        fontSize: 'clamp(38px, 5.2vw, 58px)',
                        lineHeight: 0.84,
                        letterSpacing: '-.04em',
                        color: INK,
                      }}
                    >
                      {selected.time}
                    </div>
                    <div
                      style={{
                        fontFamily: FONT_MONO,
                        fontSize: 11.5,
                        letterSpacing: '.14em',
                        textTransform: 'uppercase',
                        color: 'rgba(17,16,16,.5)',
                      }}
                    >
                      {selected.paceLabel}
                    </div>
                  </div>
                  <p style={{ margin: 0, fontSize: 16, lineHeight: 1.55, color: 'rgba(17,16,16,.74)' }}>{selected.blurb}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RunPage;
